import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, User, FileText, Scale, Clock, Users, Lock, Mail, ExternalLink } from 'lucide-react';
import SEO from '../components/SEO';
import './Privacidad.css';

const Privacidad = () => {
  const currentDate = new Date().toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Política de Privacidad - Artelaser3D",
    "description": "Política de privacidad de Artelaser3D. Información sobre protección de datos personales, derechos del usuario y tratamiento de información en nuestros servicios de grabado láser e impresión 3D en Asturias.",
    "url": "https://artelaser3d.es/privacidad",
    "lastReviewed": new Date().toISOString().split('T')[0],
    "mainEntity": {
      "@type": "Organization",
      "name": "Artelaser3D",
      "url": "https://artelaser3d.es",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Asturias",
        "addressCountry": "ES"
      }
    }
  };

  const privacySections = [
    {
      id: 'responsable',
      icon: <User size={24} />,
      title: '1. Responsable del Tratamiento',
      content: [
        'Responsable: Artelaser3D',
        'Dirección: Asturias, España',
        'Email: info@artelaser3d.es',
        'Finalidad: Gestión de pedidos, presupuestos y comunicaciones relacionadas con nuestros servicios de grabado láser e impresión 3D.',
        'En Artelaser3D nos tomamos muy en serio la protección de tus datos personales y cumplimos con el Reglamento (UE) 2016/679 (GDPR) y la Ley Orgánica 3/2018 de Protección de Datos Personales.'
      ]
    },
    {
      id: 'datos-recopilados',
      icon: <FileText size={24} />,
      title: '2. Datos Personales que Recopilamos',
      content: [
        'Datos de contacto: nombre, apellidos, email, teléfono, dirección postal.',
        'Datos de facturación: información necesaria para emitir facturas (DNI/CIF, dirección fiscal).',
        'Datos del proyecto: diseños, archivos, especificaciones técnicas y requisitos del servicio solicitado.',
        'Datos de navegación: dirección IP, tipo de navegador, sistema operativo, páginas visitadas (a través de cookies analíticas).',
        'No recopilamos datos especialmente protegidos según el artículo 9 del GDPR (datos de salud, creencias religiosas, orientación sexual, etc.).'
      ]
    },
    {
      id: 'finalidad',
      icon: <Scale size={24} />,
      title: '3. Finalidad del Tratamiento',
      content: [
        'Gestionar y procesar tus pedidos, presupuestos y solicitudes de información sobre nuestros servicios.',
        'Realizar la facturación correspondiente y gestionar los pagos.',
        'Mantener comunicaciones relacionadas con el servicio contratado.',
        'Enviar información comercial y promocional (solo con tu consentimiento explícito).',
        'Cumplir con obligaciones legales y requerimientos de las autoridades.',
        'Mejorar nuestros servicios y la experiencia de usuario en el sitio web.'
      ]
    },
    {
      id: 'base-juridica',
      icon: <Lock size={24} />,
      title: '4. Base Jurídica para el Tratamiento',
      content: [
        'Ejecución de un contrato: cuando los datos son necesarios para proporcionarte los servicios solicitados.',
        'Consentimiento del interesado: para el envío de comunicaciones comerciales y marketing.',
        'Interés legítimo: para mejorar nuestros servicios y garantizar la seguridad del sitio web.',
        'Cumplimiento de obligaciones legales: para facturación, impuestos y requerimientos administrativos.',
        'Puedes retirar tu consentimiento en cualquier momento sin que ello afecte a la licitud del tratamiento anterior.'
      ]
    }
  ];

  const userRights = [
    {
      right: 'Derecho de Acceso',
      description: 'Puedes solicitar información sobre qué datos personales tuyos estamos tratando.'
    },
    {
      right: 'Derecho de Rectificación',
      description: 'Puedes solicitar la corrección de datos inexactos o incompletos.'
    },
    {
      right: 'Derecho de Supresión',
      description: 'Puedes solicitar la eliminación de tus datos cuando ya no sean necesarios.'
    },
    {
      right: 'Derecho de Oposición',
      description: 'Puedes oponerte al tratamiento de tus datos para fines específicos.'
    },
    {
      right: 'Derecho de Limitación',
      description: 'Puedes solicitar la restricción del tratamiento en determinadas circunstancias.'
    },
    {
      right: 'Derecho de Portabilidad',
      description: 'Puedes solicitar tus datos en formato estructurado para transferirlos a otro responsable.'
    }
  ];

  return (
    <>
      <SEO 
        title="Política de Privacidad"
        description="Política de privacidad de Artelaser3D. Protección de datos personales, derechos del usuario y tratamiento de información en servicios de grabado láser e impresión 3D en Asturias."
        keywords="política de privacidad Artelaser3D, protección datos grabado láser Asturias, GDPR impresión 3D, derechos usuario datos personales"
        canonicalUrl="https://artelaser3d.es/privacidad"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="page-hero page-hero--privacy">
        <div className="page-hero__container">
          <span className="page-hero__badge">Protección de Datos</span>
          <h1 className="page-hero__title">
            Política de
            <span className="text-primary"> Privacidad</span>
          </h1>
          <p className="page-hero__description">
            Tu privacidad es nuestra prioridad. Conoce cómo protegemos y tratamos 
            tus datos personales conforme al GDPR.
          </p>
          <p className="page-hero__updated">
            Última actualización: {currentDate}
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="section section--privacy-intro section--visible">
        <div className="section__container">
          <div className="privacy-intro-card">
            <div className="privacy-intro-icon">
              <Shield size={40} />
            </div>
            <div className="privacy-intro-content">
              <h2>Compromiso con tu Privacidad</h2>
              <p>
                En <strong>Artelaser3D</strong> estamos comprometidos con la protección de tus 
                datos personales. Esta política de privacidad describe cómo recopilamos, 
                utilizamos, almacenamos y protegemos tu información cuando utilizas nuestros 
                servicios de <strong>grabado láser</strong> e <strong>impresión 3D</strong> 
                en Asturias.
              </p>
              <p>
                Cumplimos con el <strong>Reglamento General de Protección de Datos (RGPD/GDPR)</strong>{', '}
                la <strong>Ley Orgánica 3/2018</strong> de Protección de Datos Personales y garantía 
                de los derechos digitales, y demás normativa aplicable en materia de protección de datos.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Sections */}
      <section className="section section--privacy-content section--visible">
        <div className="section__container">
          <div className="privacy-grid">
            {privacySections.slice(0, 2).map((section) => (
              <article key={section.id} id={section.id} className="privacy-card">
                <div className="privacy-card__header">
                  <div className="privacy-card__icon">{section.icon}</div>
                  <h2>{section.title}</h2>
                </div>
                <div className="privacy-card__content">
                  {section.content.map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--privacy-content section--visible">
        <div className="section__container">
          <div className="privacy-grid">
            {privacySections.slice(2, 4).map((section) => (
              <article key={section.id} id={section.id} className="privacy-card">
                <div className="privacy-card__header">
                  <div className="privacy-card__icon">{section.icon}</div>
                  <h2>{section.title}</h2>
                </div>
                <div className="privacy-card__content">
                  {section.content.map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* User Rights Section */}
      <section className="section section--user-rights section--visible">
        <div className="section__container">
          <header className="section__header">
            <span className="section__badge">Tus Derechos</span>
            <h2>Derechos del Usuario</h2>
            <p>De acuerdo con la normativa vigente, tienes los siguientes derechos sobre tus datos personales:</p>
          </header>

          <div className="rights-grid">
            {userRights.map((item, index) => (
              <article key={index} className="rights-card">
                <div className="rights-card__header">
                  <Users size={24} className="rights-card__icon" />
                  <h3>{item.right}</h3>
                </div>
                <p>{item.description}</p>
              </article>
            ))}
          </div>

          <div className="rights-exercise">
            <h3>¿Cómo ejercer tus derechos?</h3>
            <p>
              Para ejercer cualquiera de estos derechos, puedes enviarnos una solicitud escrita 
              a nuestro email <strong>info@artelaser3d.es</strong> indicando claramente qué 
              derecho deseas ejercer y aportando una copia de tu documento de identidad. 
              Te responderemos en un plazo máximo de <strong>30 días</strong>.
            </p>
            <p>
              También tienes derecho a presentar una reclamación ante la <strong>Agencia Española 
              de Protección de Datos (AEPD)</strong> si consideras que el tratamiento de tus 
              datos vulnera la normativa vigente.
            </p>
            <a 
              href="https://www.aepd.es" 
              target="_blank" 
              rel="noopener noreferrer"
              className="aepd-link"
            >
              Visitar web de la AEPD
              <ExternalLink size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* Data Retention Section */}
      <section className="section section--retention section--visible">
        <div className="section__container">
          <div className="retention-card">
            <div className="retention-icon">
              <Clock size={32} />
            </div>
            <div className="retention-content">
              <h2>Conservación de Datos</h2>
              <p>
                Conservaremos tus datos personales durante el tiempo necesario para cumplir 
                con las finalidades descritas y mientras exista una relación contractual o 
                comercial entre ambas partes.
              </p>
              <ul>
                <li><strong>Datos de clientes:</strong> Mientras dure la relación comercial y durante los plazos legales de conservación (6 años según el Código Comercial).</li>
                <li><strong>Datos de facturación:</strong> 6 años conforme a la normativa fiscal.</li>
                <li><strong>Datos de contacto para marketing:</strong> Hasta que solicites la baja o pasen 2 años sin interacción.</li>
                <li><strong>Datos de navegación:</strong> 2 años máximo.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="section section--security section--visible">
        <div className="section__container">
          <div className="security-grid">
            <article className="security-card">
              <div className="security-icon">
                <Lock size={32} />
              </div>
              <h3>Medidas de Seguridad</h3>
              <p>
                Implementamos medidas técnicas y organizativas apropiadas para garantizar 
                la seguridad de tus datos personales y evitar su alteración, pérdida, 
                tratamiento o acceso no autorizado:
              </p>
              <ul>
                <li>Cifrado SSL/TLS en todas las comunicaciones</li>
                <li>Acceso restringido a datos personales</li>
                <li>Actualizaciones de seguridad periódicas</li>
                <li>Copias de seguridad regulares</li>
                <li>Formación en protección de datos al personal</li>
              </ul>
            </article>

            <article className="security-card">
              <div className="security-icon">
                <Users size={32} />
              </div>
              <h3>Destinatarios de los Datos</h3>
              <p>
                Tus datos personales podrán ser comunicados a:
              </p>
              <ul>
                <li><strong>Administraciones públicas:</strong> Cuando exista obligación legal (Hacienda, Seguridad Social, etc.).</li>
                <li><strong>Entidades bancarias:</strong> Para la gestión de pagos.</li>
                <li><strong>Empresas de transporte:</strong> Para la entrega de pedidos (solo datos necesarios).</li>
                <li><strong>Proveedores de servicios tecnológicos:</strong> Hosting, email, almacenamiento en la nube.</li>
              </ul>
              <p>No vendemos ni cedemos tus datos personales a terceros con fines comerciales.</p>
            </article>
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="section section--privacy-additional section--visible">
        <div className="section__container">
          <div className="privacy-additional-grid">
            <article className="privacy-additional-card">
              <h3>Cambios en la Política</h3>
              <p>
                Nos reservamos el derecho de modificar esta política de privacidad en cualquier 
                momento para adaptarla a novedades legislativas o cambios en nuestros servicios. 
                Te recomendamos revisarla periódicamente.
              </p>
            </article>

            <article className="privacy-additional-card">
              <h3>Contacto del DPO</h3>
              <p>
                Para cualquier consulta sobre protección de datos, puedes contactarnos:
              </p>
              <ul className="legal-contact-list">
                <li>
                  <Mail size={18} />
                  <span>Email: info@artelaser3d.es</span>
                </li>
              </ul>
            </article>

            <article className="privacy-additional-card privacy-additional-card--highlight">
              <h3>Tu Confianza es Importante</h3>
              <p>
                Al utilizar nuestros servicios, confías en nosotros tus datos personales. 
                Nos comprometemos a proteger esa información y utilizarla de manera responsable 
                y transparente.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section--cta section--cta--privacy section--visible">
        <div className="cta__content">
          <h2 className="cta__title--privacy">¿Preguntas sobre privacidad?</h2>
          <p className="cta__description--privacy">
            Si tienes cualquier duda sobre nuestra política de privacidad o cómo tratamos 
            tus datos, no dudes en contactarnos. Tu privacidad es nuestra prioridad.
          </p>
          <div className="cta__buttons">
            <Link to="/contacto" className="btn btn--primary btn--lg">
              Contactar
            </Link>
            <Link to="/cookies" className="btn btn--outline btn--lg">
              Ver Política de Cookies
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Privacidad;
